import { useEventCallback } from '@mui/material';
import { useEffect, useRef } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import useForceUpdate from '../../core/hooks/useForceUpdate';

/**
 * ****** Progressive Loading ********
 * 1. it takes a list of items and a delay and a window size
 * 2. it returns a list of items and a ref
 * 3. it uses the ref to check if the user has scrolled to the bottom of the page
 * 4. if the user has scrolled to the bottom of the page, it loads more items
 * 5. it uses a timeout to load more items after a delay
 * 6. it returns the list of items and a ref to the last item in the list
 * 7. it returns a boolean to indicate if there are more items to load
 * @param parentList
 * @param delay
 * @param window
 * @returns
 */
export const useProgressiveLoading = <T>(
  parentList: T[] = [],
  appendTimeOut: number = 5000,
  pageSize: number = 10
) => {
  const renderItemsList = useRef<T[]>([]);
  const lastPassedParentList = useRef<T[]>([]);
  const forceUpdate = useForceUpdate();

  const timeoutHook = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasMoreRecords = parentList.length > renderItemsList.current.length;

  const handleOnLoadMore = useEventCallback(() => {
    if (timeoutHook.current) {
      clearTimeout(timeoutHook.current);
    }

    if (parentList.length > renderItemsList.current.length) {
      renderItemsList.current = [...parentList.slice(0, renderItemsList.current.length + pageSize)];
      forceUpdate();
    }
  });

  const [sentryRef] = useInfiniteScroll({
    loading: false,
    hasNextPage: hasMoreRecords,
    onLoadMore: handleOnLoadMore,
    rootMargin: '0px 0px 400px 0px',
    disabled: false,
    delayInMs: 0
  });

  if (lastPassedParentList.current !== parentList) {
    lastPassedParentList.current = parentList;

    renderItemsList.current = parentList.slice(
      0,
      Math.max(pageSize, Math.min(renderItemsList.current.length, parentList.length))
    );
  }

  useEffect(() => {
    if (parentList.length > renderItemsList.current.length) {
      const timeoutId = setTimeout(handleOnLoadMore, appendTimeOut);
      timeoutHook.current = timeoutId;
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentList, appendTimeOut, handleOnLoadMore, renderItemsList.current.length]);

  return {
    items: renderItemsList.current,
    sentryRef,
    hasMoreRecords
  };
};
